import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useI18n, useLogger, useSelector } from '~Hooks';
import IconButton from '~ComponentsImproved/IconButton';
import PrinterIcon from '~Assets/icons/print.svg';
import Icon from '~ComponentsImproved/Icon';
import useModal from '~ComponentsImproved/Modal/useModal';
import SlideInPanel from '~ComponentsImproved/SlideInPanel';
import PrinterStatusPanel from './PrinterStatusPanel';
import { STATUS_COLORS, StatusType } from './constants';

const ROUTES_TO_HIDE = ['customer-display', 'express'];

interface Props {
  sendTestBong?: () => void;
  hideBorder?: boolean;
  shrink?: boolean;
}

/** Displays in a slide menu the status of printers */
const PrinterStatus: FC<Props> = ({ sendTestBong, hideBorder, shrink }) => {
  const { i18n } = useI18n();

  const printers = useSelector((state) => state.printers);

  const printQueue = useSelector((state) => state.POSPrintQueue);
  const printErrors = useMemo(() => printQueue.filter((print) => Boolean(print.error)), [
    printQueue,
  ]);

  const showStatusModal = useModal();

  const location = useLocation();
  const logger = useLogger('printer-status');

  const status: StatusType = printErrors.length
    ? 'ERROR'
    : !printers.length
    ? 'NOT_CONNECTED'
    : !printers.every((printer) => printer.online)
    ? 'WARNING'
    : 'OK';

  useEffect(() => {
    if (printErrors.length > 0) {
      logger.info('Printer errors: ' + printErrors.length, {
        printErrors,
      });
    }
  }, [printErrors.length]);

  const handleClick = useCallback(() => {
    showStatusModal.toggle();

    logger.info('Clicked printer status', {
      printErrors,
      printers,
    });
  }, [printErrors, printers]);

  const icon = useMemo(() => <Icon src={PrinterIcon} color={STATUS_COLORS[status]} />, [status]);

  const styles = useMemo(
    () => ({
      ...(hideBorder ? { borderTop: 'none' } : {}),
      ...(shrink ? { transform: 'scale(0.75)' } : {}),
    }),
    [hideBorder, shrink],
  );

  if (ROUTES_TO_HIDE.includes(location.pathname)) {
    return null;
  }

  return (
    <>
      <IconButton
        icon={icon}
        text={i18n('PrinterStatus.Title')}
        onClick={handleClick}
        styles={styles}
      />

      <SlideInPanel
        isShowing={showStatusModal.isShowing}
        hide={showStatusModal.hide}
        display="flex"
        flexDirection="column"
        gap="medium">
        <PrinterStatusPanel
          status={status}
          printers={printers}
          printErrors={printErrors}
          sendTestBong={sendTestBong}
        />
      </SlideInPanel>
    </>
  );
};

export default memo(PrinterStatus);
